import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import ContentWrapper from "../components/contentwrapper/ContentWrapper";

// --------- small reusable legend with clickable items ----------
const ChartLegend = ({ chartKey, items, visibility, onToggle }) => {
    return (
        <div className="chart-legend">
            {items.map((item) => {
                const active = visibility?.[item.key];
                return (
                    <button
                        key={item.key}
                        type="button"
                        className={`chart-legend-item ${active ? "active" : "inactive"}`}
                        onClick={() => onToggle(chartKey, item.key)}
                    >
                        <span
                            className="chart-legend-dot"
                            style={{ backgroundColor: item.color }}
                        />
                        <span className="chart-legend-label">{item.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

// ---------- helpers for formatting ----------
const formatNumber = (n) => n.toLocaleString("en-US");
const formatCurrency = (n) =>
    `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
const formatPercent = (n) => `${n.toFixed(2)}%`;

// heat color for table cells
const getHeatColor = (value, max, hue, lightMin = 25, lightMax = 90) => {
    if (!max || !value) return "transparent";
    const ratio = value / max; // 0–1
    const lightness = lightMax - (lightMax - lightMin) * ratio;
    return `hsl(${hue}, 55%, ${lightness}%)`;
};

// consistent colors for sources
const sourceColorMap = {
    "Google Ads": "#4285F4",
    "Facebook Ads": "#1877F2",
    "Instagram Ads": "#E4405F",
    "LinkedIn Ads": "#0A66C2",
    "Twitter Ads": "#1DA1F2",
    "Quora Ads": "#B92B27",
    "Bing Ads": "#008373",
    "TikTok Ads": "#00F2EA",
};

const AdsOverview = () => {
    const stats = [
        { key: "amount", label: "Amount spent", value: "$12,331.04" },
        { key: "impressions", label: "Impressions", value: "295,009" },
        { key: "clicks", label: "Clicks", value: "10,688" },
        { key: "ctr", label: "CTR", value: "3.62%" },
        { key: "cpm", label: "CPM", value: "$41.80" },
        { key: "cpc", label: "CPC", value: "$1.15" },
    ];

    // ------------------------------------------------------------------
    // TIME-SERIES DATA (top row charts)
    // ------------------------------------------------------------------
    const timeSeriesData = [
        { date: "20 Oct", spend: 250, impressions: 11000, clicks: 350, ctr: 3.2, cpc: 1.1 },
        { date: "21 Oct", spend: 180, impressions: 6500, clicks: 270, ctr: 3.8, cpc: 1.4 },
        { date: "22 Oct", spend: 720, impressions: 14000, clicks: 420, ctr: 4.1, cpc: 1.2 },
        { date: "23 Oct", spend: 330, impressions: 9800, clicks: 300, ctr: 3.1, cpc: 1.0 },
        { date: "24 Oct", spend: 260, impressions: 9000, clicks: 280, ctr: 2.9, cpc: 1.3 },
        { date: "25 Oct", spend: 390, impressions: 12000, clicks: 410, ctr: 4.5, cpc: 1.6 },
        { date: "26 Oct", spend: 500, impressions: 11000, clicks: 350, ctr: 3.9, cpc: 1.1 },
        { date: "27 Oct", spend: 470, impressions: 10500, clicks: 320, ctr: 3.7, cpc: 1.2 },
        { date: "28 Oct", spend: 320, impressions: 9400, clicks: 290, ctr: 3.3, cpc: 1.4 },
        { date: "29 Oct", spend: 360, impressions: 9800, clicks: 350, ctr: 3.9, cpc: 1.2 },
        { date: "30 Oct", spend: 540, impressions: 15000, clicks: 500, ctr: 4.8, cpc: 1.7 },
        { date: "31 Oct", spend: 400, impressions: 11200, clicks: 340, ctr: 3.5, cpc: 1.3 },
        { date: "1 Nov", spend: 290, impressions: 8600, clicks: 260, ctr: 2.8, cpc: 1.1 },
        { date: "2 Nov", spend: 310, impressions: 9100, clicks: 300, ctr: 3.1, cpc: 1.3 },
        { date: "3 Nov", spend: 380, impressions: 12000, clicks: 405, ctr: 4.1, cpc: 1.5 },
        { date: "4 Nov", spend: 450, impressions: 13000, clicks: 430, ctr: 4.3, cpc: 1.4 },
        { date: "5 Nov", spend: 330, impressions: 9000, clicks: 290, ctr: 3.2, cpc: 1.1 },
    ];

    // ------------------------------------------------------------------
    // SOURCE-LEVEL DATA FOR STACKED CHART + HEATMAP TABLE + CPC CHART
    // ------------------------------------------------------------------
    const sourceRows = [
        { source: "Google Ads", impressions: 74856, clicks: 4600, ctr: 6.15, amount: 8076.0, cpc: 1.76, cpm: 107.89 },
        { source: "Twitter Ads", impressions: 36280, clicks: 2375, ctr: 6.55, amount: 2606.0, cpc: 1.10, cpm: 71.83 },
        { source: "Quora Ads", impressions: 12541, clicks: 716, ctr: 5.71, amount: 814.0, cpc: 1.14, cpm: 64.91 },
        { source: "LinkedIn Ads", impressions: 66304, clicks: 1446, ctr: 2.18, amount: 312.04, cpc: 0.22, cpm: 4.71 },
        { source: "Facebook Ads", impressions: 70912, clicks: 858, ctr: 1.36, amount: 328.68, cpc: 0.38, cpm: 4.64 },
        { source: "Bing Ads", impressions: 4216, clicks: 162, ctr: 3.84, amount: 194.0, cpc: 1.20, cpm: 45.98 },
        { source: "Instagram Ads", impressions: 1400, clicks: 80, ctr: 5.71, amount: 100.32, cpc: 1.25, cpm: 71.66 },
        { source: "TikTok Ads", impressions: 990, clicks: 44, ctr: 4.44, amount: 200.0, cpc: 4.55, cpm: 202.02 },
    ];

    // date x source stacked spends (dummy)
    const spendByDateSource = [
        {
            period: "Oct 6–12 (W41)",
            google: 80,
            facebook: 20,
            bing: 5,
            instagram: 0,
            linkedin: 10,
            quora: 5,
            tiktok: 0,
            twitter: 15,
        },
        {
            period: "Oct 13–19 (W42)",
            google: 1200,
            facebook: 150,
            bing: 30,
            instagram: 40,
            linkedin: 60,
            quora: 50,
            tiktok: 0,
            twitter: 500,
        },
        {
            period: "Oct 20–26 (W43)",
            google: 1800,
            facebook: 300,
            bing: 45,
            instagram: 60,
            linkedin: 80,
            quora: 90,
            tiktok: 40,
            twitter: 700,
        },
        {
            period: "Oct 27–Nov 2 (W44)",
            google: 1600,
            facebook: 260,
            bing: 40,
            instagram: 70,
            linkedin: 70,
            quora: 80,
            tiktok: 20,
            twitter: 650,
        },
        {
            period: "Nov 3–9 (W45)",
            google: 1300,
            facebook: 240,
            bing: 30,
            instagram: 60,
            linkedin: 60,
            quora: 70,
            tiktok: 15,
            twitter: 550,
        },
        {
            period: "Nov 10–16 (W46)",
            google: 200,
            facebook: 80,
            bing: 10,
            instagram: 10,
            linkedin: 20,
            quora: 15,
            tiktok: 5,
            twitter: 60,
        },
    ];

    // campaign type spends (dummy)
    const spendByCampaignType = [
        {
            period: "Oct 6–12 (W41)",
            engagement: 50,
            maxConversion: 20,
            traffic: 40,
            twitter: 10,
            video: 0,
            quora: 5,
            bing: 5,
        },
        {
            period: "Oct 13–19 (W42)",
            engagement: 900,
            maxConversion: 400,
            traffic: 600,
            twitter: 150,
            video: 80,
            quora: 120,
            bing: 60,
        },
        {
            period: "Oct 20–26 (W43)",
            engagement: 1100,
            maxConversion: 500,
            traffic: 900,
            twitter: 200,
            video: 150,
            quora: 160,
            bing: 80,
        },
        {
            period: "Oct 27–Nov 2 (W44)",
            engagement: 1000,
            maxConversion: 450,
            traffic: 850,
            twitter: 190,
            video: 120,
            quora: 140,
            bing: 70,
        },
        {
            period: "Nov 3–9 (W45)",
            engagement: 900,
            maxConversion: 420,
            traffic: 800,
            twitter: 170,
            video: 110,
            quora: 130,
            bing: 60,
        },
        {
            period: "Nov 10–16 (W46)",
            engagement: 150,
            maxConversion: 70,
            traffic: 120,
            twitter: 40,
            video: 20,
            quora: 25,
            bing: 10,
        },
    ];

    // cost per click by date & source
    const cpcByDateSource = spendByDateSource.map((row, index) => ({
        period: row.period,
        google: 1.5 + index * 0.1,
        facebook: 1.1 + index * 0.05,
        bing: 1.2,
        instagram: 1.3,
        linkedin: 0.9 + index * 0.03,
        quora: 1.4 + index * 0.02,
        tiktok: 2.5 + index * 0.3,
        twitter: 1.0 + index * 0.08,
    }));

    // ------------------------------------------------------------------
    // CAMPAIGN SUMMARY DATA (for pie + horizontal bars + summary table)
    // ------------------------------------------------------------------

    const campaignSummaryRows = [
        {
            name: "GA: Branding campaign #1",
            type: "Traffic",
            source: "Google Ads",
            amount: 3233.0,
            impressions: 29888,
            clicks: 1808,
            ctr: 6.05,
            cpc: 1.79,
            cpm: 108.17,
        },
        {
            name: "Twitter Campaign",
            type: "Twitter",
            source: "Twitter Ads",
            amount: 2606.0,
            impressions: 36280,
            clicks: 2375,
            ctr: 6.55,
            cpc: 1.1,
            cpm: 71.83,
        },
        {
            name: "GA: Branding campaign #2",
            type: "Traffic",
            source: "Facebook Ads",
            amount: 2595.0,
            impressions: 22321,
            clicks: 1293,
            ctr: 5.79,
            cpc: 2.01,
            cpm: 116.26,
        },
        {
            name: "GA: Branding campaign #3",
            type: "Traffic",
            source: "Linkdin Ads",
            amount: 1091.0,
            impressions: 8794,
            clicks: 554,
            ctr: 6.3,
            cpc: 1.97,
            cpm: 124.96,
        },
        {
            name: "Quora Ads Campaign",
            type: "Quora Ads",
            source: "Quora Ads",
            amount: 814.0,
            impressions: 12541,
            clicks: 716,
            ctr: 5.71,
            cpc: 1.14,
            cpm: 64.91,
        },
        {
            name: "GA: Branding campaign #4",
            type: "Traffic",
            source: "Bings Ads",
            amount: 629.0,
            impressions: 4831,
            clicks: 317,
            ctr: 6.56,
            cpc: 1.96,
            cpm: 128.34,
        },
    ];

    // ---------------- visibility state for time-series charts -------------
    const [legendVisibility, setLegendVisibility] = useState({
        chart2: { impressions: true, clicks: true },
        chart3: { impressions: true, ctr: false }, // CTR off initially
        chart4: { clicks: true, cpc: true },
    });

    const handleToggleLegend = (chartKey, seriesKey) => {
        setLegendVisibility((prev) => ({
            ...prev,
            [chartKey]: {
                ...prev[chartKey],
                [seriesKey]: !prev[chartKey][seriesKey],
            },
        }));
    };

    // ---------------------- source table sorting + totals -----------------
    const [sortConfig, setSortConfig] = useState({
        key: "amount",
        direction: "desc",
    });

    const maxValues = useMemo(
        () => ({
            impressions: Math.max(...sourceRows.map((r) => r.impressions)),
            clicks: Math.max(...sourceRows.map((r) => r.clicks)),
            ctr: Math.max(...sourceRows.map((r) => r.ctr)),
            amount: Math.max(...sourceRows.map((r) => r.amount)),
            cpc: Math.max(...sourceRows.map((r) => r.cpc)),
            cpm: Math.max(...sourceRows.map((r) => r.cpm)),
        }),
        []
    );

    const sortedSources = useMemo(() => {
        const rows = [...sourceRows];
        const { key, direction } = sortConfig;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av < bv) return direction === "asc" ? -1 : 1;
            if (av > bv) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return rows;
    }, [sortConfig]);

    const sourceTotals = useMemo(() => {
        const t = sourceRows.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.amount += row.amount;
                return acc;
            },
            { impressions: 0, clicks: 0, amount: 0 }
        );
        const ctr = (t.clicks / t.impressions) * 100;
        const cpc = t.amount / t.clicks;
        const cpm = (t.amount / t.impressions) * 1000;
        return { ...t, ctr, cpc, cpm };
    }, []);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "desc" };
        });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key !== key) return <span className="sort-arrow">↕</span>;
        return (
            <span className="sort-arrow">
                {sortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    // -------------------- derived data for clicks/campaign charts ---------
    const clicksBySourceData = useMemo(() => {
        const map = {};
        campaignSummaryRows.forEach((row) => {
            if (!map[row.source]) {
                map[row.source] = { source: row.source, clicks: 0 };
            }
            map[row.source].clicks += row.clicks;
        });
        return Object.values(map);
    }, []);

    const clicksByCampaignTypeData = useMemo(() => {
        const map = {};
        campaignSummaryRows.forEach((row) => {
            if (!map[row.type]) {
                map[row.type] = { campaignType: row.type, clicks: 0 };
            }
            map[row.type].clicks += row.clicks;
        });
        return Object.values(map);
    }, []);

    const cpcByCampaignTypeData = useMemo(() => {
        const map = {};
        campaignSummaryRows.forEach((row) => {
            if (!map[row.type]) {
                map[row.type] = {
                    campaignType: row.type,
                    amount: 0,
                    clicks: 0,
                };
            }
            map[row.type].amount += row.amount;
            map[row.type].clicks += row.clicks;
        });
        return Object.values(map).map((row) => ({
            campaignType: row.campaignType,
            cpc: row.amount / Math.max(row.clicks, 1),
        }));
    }, []);

    // -------------------- summary table sorting + totals ------------------
    const [summarySortConfig, setSummarySortConfig] = useState({
        key: "amount",
        direction: "desc",
    });

    const summaryMaxValues = useMemo(
        () => ({
            amount: Math.max(...campaignSummaryRows.map((r) => r.amount)),
            impressions: Math.max(...campaignSummaryRows.map((r) => r.impressions)),
            clicks: Math.max(...campaignSummaryRows.map((r) => r.clicks)),
            ctr: Math.max(...campaignSummaryRows.map((r) => r.ctr)),
            cpc: Math.max(...campaignSummaryRows.map((r) => r.cpc)),
            cpm: Math.max(...campaignSummaryRows.map((r) => r.cpm)),
        }),
        []
    );

    const sortedCampaignSummaryRows = useMemo(() => {
        const rows = [...campaignSummaryRows];
        const { key, direction } = summarySortConfig;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av < bv) return direction === "asc" ? -1 : 1;
            if (av > bv) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return rows;
    }, [summarySortConfig]);

    const summaryTotals = useMemo(() => {
        const t = campaignSummaryRows.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.amount += row.amount;
                return acc;
            },
            { impressions: 0, clicks: 0, amount: 0 }
        );
        const ctr = (t.clicks / t.impressions) * 100;
        const cpc = t.amount / Math.max(t.clicks, 1);
        const cpm = (t.amount / Math.max(t.impressions, 1)) * 1000;
        return { ...t, ctr, cpc, cpm };
    }, []);

    const handleSummarySort = (key) => {
        setSummarySortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "desc" };
        });
    };

    const renderSummarySortArrow = (key) => {
        if (summarySortConfig.key !== key) return <span className="sort-arrow">↕</span>;
        return (
            <span className="sort-arrow">
                {summarySortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <div className="ads-overview-page">
            <Helmet>
                <title>SATKARTAR: ADS OVERVIEW</title>
            </Helmet>

            <ContentWrapper>
                {/* TOP METRIC CARDS */}
                <div className="ads-overview-cards row g-3 mb-4">
                    {stats.map((item) => (
                        <div
                            key={item.key}
                            className="col-6 col-md-4 col-xl-2 d-flex"
                        >
                            <div className="ads-metric-card w-100">
                                <div className="ads-metric-label">{item.label}</div>
                                <div className="ads-metric-value">{item.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* FIRST BLOCK: existing time-series & spend charts */}
                <div className="row g-4" style={{ marginBottom: "55px" }}>
                    {/* 1: Amount spent over time */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Amount spent by Date</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={timeSeriesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip
                                        contentStyle={{ color: "#1F2937" }}
                                        labelStyle={{ color: "#1F2937" }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="spend"
                                        stroke="#22c55e"
                                        strokeWidth={2}
                                        dot={{ stroke: "#22c55e", strokeWidth: 2 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 2: Impressions vs Clicks */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Impressions and Clicks by Date</h5>
                                <ChartLegend
                                    chartKey="chart2"
                                    visibility={legendVisibility.chart2}
                                    onToggle={handleToggleLegend}
                                    items={[
                                        { key: "impressions", label: "Impressions", color: "#0ea5e9" },
                                        { key: "clicks", label: "Clicks", color: "#dc2626" },
                                    ]}
                                />
                            </div>

                            <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={timeSeriesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip />
                                    {legendVisibility.chart2.impressions && (
                                        <Line
                                            dataKey="impressions"
                                            stroke="#0ea5e9"
                                            strokeWidth={2}
                                        />
                                    )}
                                    {legendVisibility.chart2.clicks && (
                                        <Line
                                            dataKey="clicks"
                                            stroke="#dc2626"
                                            strokeWidth={2}
                                        />
                                    )}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 3: Impressions + CTR */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Impressions and CTR over time</h5>
                                <ChartLegend
                                    chartKey="chart3"
                                    visibility={legendVisibility.chart3}
                                    onToggle={handleToggleLegend}
                                    items={[
                                        { key: "impressions", label: "Impressions", color: "#0284c7" },
                                        { key: "ctr", label: "CTR", color: "#6b7280" },
                                    ]}
                                />
                            </div>

                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={timeSeriesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip />
                                    {legendVisibility.chart3.impressions && (
                                        <Bar dataKey="impressions" fill="#0284c7" />
                                    )}
                                    {legendVisibility.chart3.ctr && (
                                        <Line
                                            type="monotone"
                                            dataKey="ctr"
                                            stroke="#f59e0b"
                                            strokeWidth={2}
                                        />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 4: Clicks + CPC */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Clicks and performance over time</h5>
                                <ChartLegend
                                    chartKey="chart4"
                                    visibility={legendVisibility.chart4}
                                    onToggle={handleToggleLegend}
                                    items={[
                                        { key: "clicks", label: "Clicks", color: "#b91c1c" },
                                        { key: "cpc", label: "CPC", color: "#6b7280" },
                                    ]}
                                />
                            </div>

                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={timeSeriesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip />
                                    {legendVisibility.chart4.clicks && (
                                        <Bar dataKey="clicks" fill="#b91c1c" />
                                    )}
                                    {legendVisibility.chart4.cpc && (
                                        <Line
                                            type="monotone"
                                            dataKey="cpc"
                                            stroke="#6b7280"
                                            strokeWidth={2}
                                        />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* stacked bar: amount spent by date & source */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Amount spent by Date and Source</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={spendByDateSource}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip />
                                    <Legend
                                        verticalAlign="top"
                                        align="left"
                                        iconType="circle"
                                        wrapperStyle={{ paddingBottom: 8 }}
                                    />
                                    <Bar dataKey="bing" stackId="a" fill="#2563eb" name="Bing Ads" />
                                    <Bar dataKey="facebook" stackId="a" fill="#ef4444" name="Facebook Ads" />
                                    <Bar dataKey="google" stackId="a" fill="#facc15" name="Google Ads" />
                                    <Bar dataKey="instagram" stackId="a" fill="#ec4899" name="Instagram Ads" />
                                    <Bar dataKey="linkedin" stackId="a" fill="#22c55e" name="LinkedIn Ads" />
                                    <Bar dataKey="quora" stackId="a" fill="#6366f1" name="Quora Ads" />
                                    <Bar dataKey="tiktok" stackId="a" fill="#14b8a6" name="TikTok Ads" />
                                    <Bar dataKey="twitter" stackId="a" fill="#0ea5e9" name="Twitter Ads" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Source performance table */}
                    <div className="col-md-6">
                        <div className="chart-card source-table-card">
                            <div className="chart-card-header">
                                <h5>Source performance</h5>
                            </div>

                            <div className="source-table-wrapper">
                                <div className="source-table-scroll">
                                    <table className="source-table">
                                        <thead>
                                            <tr>
                                                <th>Source</th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSort("impressions")}
                                                >
                                                    Impressions {renderSortArrow("impressions")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSort("clicks")}
                                                >
                                                    Clicks {renderSortArrow("clicks")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSort("ctr")}
                                                >
                                                    CTR {renderSortArrow("ctr")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSort("amount")}
                                                >
                                                    Amount spent {renderSortArrow("amount")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSort("cpc")}
                                                >
                                                    CPC {renderSortArrow("cpc")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSort("cpm")}
                                                >
                                                    CPM {renderSortArrow("cpm")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedSources.map((row) => (
                                                <tr key={row.source}>
                                                    <td>{row.source}</td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.impressions,
                                                                maxValues.impressions,
                                                                220
                                                            ),
                                                        }}
                                                    >
                                                        {formatNumber(row.impressions)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.clicks,
                                                                maxValues.clicks,
                                                                200
                                                            ),
                                                        }}
                                                    >
                                                        {formatNumber(row.clicks)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.ctr,
                                                                maxValues.ctr,
                                                                280
                                                            ),
                                                        }}
                                                    >
                                                        {formatPercent(row.ctr)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.amount,
                                                                maxValues.amount,
                                                                45
                                                            ),
                                                        }}
                                                    >
                                                        {formatCurrency(row.amount)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.cpc,
                                                                maxValues.cpc,
                                                                160
                                                            ),
                                                        }}
                                                    >
                                                        {formatCurrency(row.cpc)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.cpm,
                                                                maxValues.cpm,
                                                                330
                                                            ),
                                                        }}
                                                    >
                                                        {formatCurrency(row.cpm)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td>Grand total</td>
                                                <td>{formatNumber(sourceTotals.impressions)}</td>
                                                <td>{formatNumber(sourceTotals.clicks)}</td>
                                                <td>{formatPercent(sourceTotals.ctr)}</td>
                                                <td>{formatCurrency(sourceTotals.amount)}</td>
                                                <td>{formatCurrency(sourceTotals.cpc)}</td>
                                                <td>{formatCurrency(sourceTotals.cpm)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Amount spent by campaign type */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Amount spent by Campaign type</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={spendByCampaignType}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip />
                                    <Legend
                                        verticalAlign="top"
                                        align="left"
                                        iconType="circle"
                                        wrapperStyle={{ paddingBottom: 8 }}
                                    />
                                    <Bar dataKey="bing" stackId="b" fill="#2563eb" name="Bing Ads" />
                                    <Bar dataKey="engagement" stackId="b" fill="#0ea5e9" name="Engagement" />
                                    <Bar
                                        dataKey="maxConversion"
                                        stackId="b"
                                        fill="#f97316"
                                        name="Max conversion"
                                    />
                                    <Bar dataKey="quora" stackId="b" fill="#6366f1" name="Quora Ads" />
                                    <Bar dataKey="traffic" stackId="b" fill="#22c55e" name="Traffic" />
                                    <Bar dataKey="twitter" stackId="b" fill="#1d4ed8" name="Twitter" />
                                    <Bar dataKey="video" stackId="b" fill="#eab308" name="Video Views" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* CPC by date & source */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Cost per click by Date and Source</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={cpcByDateSource}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" tick={{ fill: "#1F2937" }} />
                                    <YAxis tick={{ fill: "#1F2937" }} />
                                    <Tooltip />
                                    <Legend
                                        verticalAlign="top"
                                        align="left"
                                        iconType="circle"
                                        wrapperStyle={{ paddingBottom: 8 }}
                                    />
                                    <Bar dataKey="bing" fill="#2563eb" name="Bing Ads" />
                                    <Bar dataKey="facebook" fill="#ef4444" name="Facebook Ads" />
                                    <Bar dataKey="google" fill="#facc15" name="Google Ads" />
                                    <Bar dataKey="instagram" fill="#ec4899" name="Instagram Ads" />
                                    <Bar dataKey="linkedin" fill="#22c55e" name="LinkedIn Ads" />
                                    <Bar dataKey="quora" fill="#6366f1" name="Quora Ads" />
                                    <Bar dataKey="tiktok" fill="#14b8a6" name="TikTok Ads" />
                                    <Bar dataKey="twitter" fill="#0ea5e9" name="Twitter Ads" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    {/* Clicks by Source – donut */}
                    <div className="col-lg-4 col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Clicks by Source</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie
                                        data={clicksBySourceData}
                                        dataKey="clicks"
                                        nameKey="source"
                                        cx="40%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={90}
                                        paddingAngle={2}
                                    >
                                        {clicksBySourceData.map((entry, index) => {
                                            const color =
                                                sourceColorMap[entry.source] ||
                                                Object.values(sourceColorMap)[
                                                index % Object.values(sourceColorMap).length
                                                ];
                                            return (
                                                <Cell
                                                    key={entry.source}
                                                    fill={color}
                                                />
                                            );
                                        })}
                                    </Pie>
                                    <Tooltip />
                                    <Legend
                                        layout="vertical"
                                        verticalAlign="middle"
                                        align="right"
                                        iconType="circle"
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Clicks by Campaign type – horizontal bar */}
                    <div className="col-lg-4 col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Clicks by Campaign type</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart
                                    data={clicksByCampaignTypeData}
                                    layout="vertical"
                                    margin={{ left: 60, right: 16, top: 16, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" tick={{ fill: "#1F2937" }} />
                                    <YAxis
                                        type="category"
                                        dataKey="campaignType"
                                        tick={{ fontSize: 12, fill: "#1F2937" }}
                                    />
                                    <Tooltip />
                                    <Legend />
                                    <Bar
                                        dataKey="clicks"
                                        name="Clicks"
                                        fill="#0ea5e9"
                                        barSize={14}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* CPC by Campaign type – horizontal bar */}
                    <div className="col-lg-4 col-md-12">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>CPC by Campaign type</h5>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart
                                    data={cpcByCampaignTypeData}
                                    layout="vertical"
                                    margin={{ left: 80, right: 16, top: 16, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type="number"
                                        tickFormatter={(v) => `$${v.toFixed(2)}`}
                                        tick={{ fill: "#1F2937" }}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="campaignType"
                                        tick={{ fontSize: 12, fill: "#1F2937" }}

                                    />
                                    <Tooltip
                                        formatter={(value) => formatCurrency(value)}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="cpc"
                                        name="CPC"
                                        fill="#0891b2"
                                        barSize={14}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    {/* Summary table */}
                    <div className="col-12">
                        <div className="chart-card summary-table-card">
                            <div className="chart-card-header">
                                <h5>Summary table</h5>
                            </div>

                            <div className="source-table-wrapper">
                                <div className="source-table-scroll">
                                    <table className="source-table">
                                        <thead>
                                            <tr>
                                                <th>Campaign name</th>
                                                <th>Campaign type</th>
                                                <th>Source</th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSummarySort("amount")}
                                                >
                                                    Amount spent {renderSummarySortArrow("amount")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSummarySort("impressions")}
                                                >
                                                    Impressions{" "}
                                                    {renderSummarySortArrow("impressions")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSummarySort("clicks")}
                                                >
                                                    Clicks {renderSummarySortArrow("clicks")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSummarySort("ctr")}
                                                >
                                                    CTR {renderSummarySortArrow("ctr")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSummarySort("cpc")}
                                                >
                                                    CPC {renderSummarySortArrow("cpc")}
                                                </th>
                                                <th
                                                    className="sortable-th"
                                                    onClick={() => handleSummarySort("cpm")}
                                                >
                                                    CPM {renderSummarySortArrow("cpm")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedCampaignSummaryRows.map((row) => (
                                                <tr key={row.name}>
                                                    <td>{row.name}</td>
                                                    <td>{row.type}</td>
                                                    <td>{row.source}</td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.amount,
                                                                summaryMaxValues.amount,
                                                                45
                                                            ),
                                                        }}
                                                    >
                                                        {formatCurrency(row.amount)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.impressions,
                                                                summaryMaxValues.impressions,
                                                                220
                                                            ),
                                                        }}
                                                    >
                                                        {formatNumber(row.impressions)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.clicks,
                                                                summaryMaxValues.clicks,
                                                                200
                                                            ),
                                                        }}
                                                    >
                                                        {formatNumber(row.clicks)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.ctr,
                                                                summaryMaxValues.ctr,
                                                                160
                                                            ),
                                                        }}
                                                    >
                                                        {formatPercent(row.ctr)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.cpc,
                                                                summaryMaxValues.cpc,
                                                                300
                                                            ),
                                                        }}
                                                    >
                                                        {formatCurrency(row.cpc)}
                                                    </td>
                                                    <td
                                                        className="heat-cell"
                                                        style={{
                                                            background: getHeatColor(
                                                                row.cpm,
                                                                summaryMaxValues.cpm,
                                                                330
                                                            ),
                                                        }}
                                                    >
                                                        {formatCurrency(row.cpm)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan={3}>Grand total</td>
                                                <td>{formatCurrency(summaryTotals.amount)}</td>
                                                <td>{formatNumber(summaryTotals.impressions)}</td>
                                                <td>{formatNumber(summaryTotals.clicks)}</td>
                                                <td>{formatPercent(summaryTotals.ctr)}</td>
                                                <td>{formatCurrency(summaryTotals.cpc)}</td>
                                                <td>{formatCurrency(summaryTotals.cpm)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default AdsOverview;
